/*
Toggle Class

### Html Attributes

```
  data-toggle-element-classes
  data-toggle-element-classes--target="[(scoping-directive)] .valid-jquery > selector"
  data-toggle-element-classes--on="valid jquery event name"
  data-toggle-element-classes--remove="space separated class name(s)"
  data-toggle-element-classes--remove-from="[(scoping-directive)] .valid-jquery > selector"
  data-toggle-element-classes--delegate="true|false, bind event on element, or on document root"
  data-toggle-element-classes--ignore-child-events="true|false, child triggers events"
  data-toggle-element-classes--to="space separated class name(s)"
```

#### scoping directives

Scoping directives control where or what direction in the dom the selector is applied

- `(parent)` Find a parent
- `(parents)` Find all parents
- `(here)` Find siblings or descendants
- `(siblings)` Find siblings


Markup:
{{#> Core.Styleguide.Box
    modifier_class="toggle-class-box u-align u-align--horizontal u-align--horizontal-center"
    width="100%" height="5rem" grow="0"}}
<button class="button"
  data-toggle-element-classes
  data-toggle-element-classes--to="active">
  simple toggle
</button>
<button class="button"
  data-toggle-element-classes
  data-toggle-element-classes--to="button--primary">
  toggle many
</button>
<button class="button"
  data-toggle-element-classes
  data-toggle-element-classes--target="(parent) .toggle-class-box"
  data-toggle-element-classes--to="styleguide-box--green">
  toggle other element
</button>
{{/Core.Styleguide.Box}}

Styleguide Global.Javascript.ToggleClass
*/
import $ from 'jquery';

import debug from 'debug';
import plugin from '@core/plugins/lib';
import { ELEMENT_SELECTOR, PLUGIN_NAME } from './index';


const log = debug(`plugins/${PLUGIN_NAME}`);

class PluginKlass {

  constructor(element, options) {
    this.element = element;
    this.$element = $(element);
    this.options = options;

    this.$classes = this.options.to.split(',') || [];
    this.$targets = this.options.target.split(',') || [];

    log('construct', element, options);

    this.watch();

  }

  watch () {
    const { on, target, delegate} = this.options;
    if (on === '(now)') {
      this.toggle();
    } else if (!delegate) {
      this.boundEvent = this.$element
        .on(on, this.filterEvents(this.toggle));
    } else {
      $(document)
        .on(on, this.element, this.filterEvents(this.toggle));
    }
    log('watching', this.element, { on, target, delegate });
  }


	// filter non delegate events
	// allow default events (and do nothing) clicking with modifier key on links
	filterEvents(fn) {
		const { ignoreChildEvents } = this.options;
		return event => {
			if (ignoreChildEvents && event.target !== this.element) {
				return;
			} else if (this.element.nodeName.toLowerCase() !== 'a') {
				fn.call(this);
			} else if (event.ctrlKey ||
				event.metaKey ||
				event.shiftKey ||
				event.altKey) {
					return true;
			} else {
				event.preventDefault();
				fn.call(this);
			}
		};
	}


  toggle (event) {
    const {
      target,
      to,
    } = this.options;

    if (this.options.preventDefault) {
      event.preventDefault();
    }

    if (this.options.stopPropagation) {
      event.stopPropagation();
    }

    // compile a list of targets that do or don't have it.
    // because next event could result in a remove class
    const $has = this.$targets.map(target => {
      const matching = this.selectors.selectTarget(target, this.$element)
          .attr('class')
          .split(' ')
          .filter(item => this.$classes.includes(item));
      return matching && matching.length > 0;
    });

    this.$element.trigger(`${PLUGIN_NAME}.before`, this.options);
    this.$targets.forEach((target, index) => {
      const $target = this.selectors.selectTarget(target, this.$element);
      const $classes = this.$classes[index];

      $target.trigger(`${PLUGIN_NAME}.beforeTarget`, this.options);
      if ($has[index] && $classes) {
        log('toggle.remove', { $target, $classes, $has: $has[index]});
        $target.removeClass($classes);
      } else if(!$has[index] && $classes) {
        log('toggle.add', {$target, $classes, $has: $has[index]});
        $target.addClass($classes);
      }
      $target.trigger(`${PLUGIN_NAME}.afterTarget`, this.options);
      log('toggle', this.options);

    });
    this.$element.trigger(`${PLUGIN_NAME}.after`, this.options);

    if (this.options.preventDefault) {
      return false;
    }

  }

}

PluginKlass.DEFAULTS = {
  on: 'click', // event which current element is triggered
  target: '', // selector for the target to toggle classnames on
  to: '', // the classname to toggle
  delegate: false, // bind on element or use delegate events on document.
  ignoreChildEvents: false, // whether or not child events trigger
};

export default PluginKlass;
//plugin(PLUGIN_NAME, PluginKlass);



// WEBPACK FOOTER //
// ./project/client/src/core/plugins/jquery__toggle-element-classes/plugin.js